/**
 * Web/desktop host bindings for the offline write queue.
 *
 * The queue's rules live in ./offline-queue, shared verbatim with mobile. This
 * file supplies the three genuinely host-specific pieces: where the ledger is
 * stored, how an HTTP status is dug out of the client's error type, and the
 * single FIFO gate every replay passes through.
 *
 * The ledger uses localStorage rather than the SQLite/IndexedDB query cache on
 * purpose. It is a handful of small rows that must be readable synchronously
 * before the first paint, and it has to survive the cache being busted by a
 * deploy — the read cache is keyed by build id, and a queued write must not
 * disappear because the app shipped a new version overnight.
 */
import { ApiError } from '@/lib/api/api-client'

import type { OfflineLedgerEntry, OfflineRunnerContext } from './offline-queue'
import { createOfflineLedger, createSerialGate } from './offline-queue'

const LEDGER_STORAGE_KEY = 'scrambl-offline-write-ledger-v1'

function persistLedger(entries: OfflineLedgerEntry[]): void {
  try {
    if (entries.length === 0) {
      window.localStorage.removeItem(LEDGER_STORAGE_KEY)
      return
    }
    window.localStorage.setItem(LEDGER_STORAGE_KEY, JSON.stringify(entries))
  } catch (error) {
    console.warn('[OfflineQueue] Failed to persist ledger', error)
  }
}

export const offlineLedger = createOfflineLedger(persistLedger)

/**
 * Restore queued and blocked rows from a previous session. Runs before paused
 * mutations are resumed — a resumed write whose ledger row is missing looks
 * brand new, which is exactly the duplicate the ledger exists to prevent.
 */
export function restoreOfflineLedger(): void {
  try {
    const raw = window.localStorage.getItem(LEDGER_STORAGE_KEY)
    if (!raw) return

    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return

    const entries = parsed.filter(
      (entry): entry is OfflineLedgerEntry =>
        typeof entry === 'object' &&
        entry !== null &&
        'envelope' in entry &&
        typeof (entry as OfflineLedgerEntry).envelope?.idempotencyKey ===
          'string',
    )

    offlineLedger.hydrate(entries)
    offlineLedger.prune()
  } catch (error) {
    console.warn('[OfflineQueue] Failed to restore ledger', error)
  }
}

export function clearOfflineLedger(): void {
  offlineLedger.list().forEach(entry => {
    offlineLedger.remove(entry.envelope.idempotencyKey)
  })
  try {
    window.localStorage.removeItem(LEDGER_STORAGE_KEY)
  } catch {
    // Storage disabled — nothing to clear.
  }
}

/**
 * A fetch that never reached a server throws a TypeError with no status, and
 * "no status" is precisely the signal `classifyReplayError` needs in order to
 * keep a write queued instead of failing it.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status
  const status = (error as { status?: unknown } | null)?.status
  return typeof status === 'number' ? status : undefined
}

export const offlineRunnerContext: OfflineRunnerContext = {
  ledger: offlineLedger,
  gate: createSerialGate(),
  getStatus: getErrorStatus,
}
